import React, { useState, useEffect, useRef } from 'react';
import '../App.css';
import { HUMAN_PLAYER_ID } from '../gameLogic/constants.js';
import useAnimationManager from '../hooks/useAnimationManager.js';
import { getEffectiveScale, getUILoc, getPlayerName, getCardName} from '../utils/cardUtils.js';
import { CardIllustration } from './Card.js';
import CardDetail from './CardDetail.js';
import audioManager from '../utils/AudioManager.js';

const PlayerStats = ({ player, gameState, onEndTurn, onCardClick, showSubInfo = false }) => {
    const [showDetails, setShowDetails] = useState(false);
    const [pileDetailType, setPileDetailType] = useState(null); // 'deck', 'discard', or null
    const [selectedDetailCard, setSelectedDetailCard] = useState(null);
    const [isDeckFlashing, setIsDeckFlashing] = useState(false);
    const [helpTarget, setHelpTarget] = useState(null); // 'consciousness', 'scale', or null
    const [helpPosition, setHelpPosition] = useState({ x: 0, y: 0 });
    const { triggerEffect } = useAnimationManager(gameState);
    
    const deckFlashTimerRef = useRef(null);
    
    // 前回の値を記録（変化検知用）
    const prevValuesRef = useRef({
        consciousness: null,
        scale: null,
        deckLength: null
    });
    
    const playerConsciousness = player?.consciousness;
    const playerScale = getEffectiveScale(player, gameState);
    const playerId = player?.id;
    const deckLength = player?.deck?.length ?? 0;

    // アンマウント時にタイマーをクリアする
    useEffect(() => {
        return () => {
            if (deckFlashTimerRef.current) {
                clearTimeout(deckFlashTimerRef.current);
            }
        };
    }, []);

    // リソース変化の検知と演出トリガー
    useEffect(() => {
        if (!player) return;
        
        const currentConsciousness = playerConsciousness;
        const currentScale = playerScale;
        const currentDeckLength = deckLength;
        const prevValues = prevValuesRef.current;
        
        // 意識の変化をチェック
        if (prevValues.consciousness !== null && prevValues.consciousness !== currentConsciousness) {
            const changeAmount = currentConsciousness - prevValues.consciousness;
            
            const consciousnessElement = document.querySelector(`[data-player-id="${playerId}"] .consciousness .stat-value`);
            if (consciousnessElement && triggerEffect) {
                const effectType = 'CONSCIOUSNESS_CHANGE_RESULT';
                triggerEffect(effectType, consciousnessElement, {
                    effect: {
                        args: {
                            amount: changeAmount,
                            player_id: playerId
                        }
                    }
                });
            }
        }
        
        // 規模の変化をチェック
        if (prevValues.scale !== null && prevValues.scale !== currentScale) {
            const changeAmount = currentScale - prevValues.scale;
            
            const scaleElement = document.querySelector(`[data-player-id="${playerId}"] .scale .stat-value`);
            if (scaleElement && triggerEffect) {
                const effectType = 'SCALE_CHANGE_RESULT';
                triggerEffect(effectType, scaleElement, {
                    effect: {
                        args: {
                            amount: changeAmount,
                            player_id: playerId
                        }
                    }
                });
            }
        }

        // デッキ枚数の変化をチェック
        if (prevValues.deckLength !== null && currentDeckLength < prevValues.deckLength) {
            const isDropTo10OrLess = prevValues.deckLength > 10 && currentDeckLength <= 10;
            const isDecreaseIn5OrLess = currentDeckLength <= 5;
            
            if (isDropTo10OrLess || isDecreaseIn5OrLess) {
                setIsDeckFlashing(true);
                if (deckFlashTimerRef.current) {
                    clearTimeout(deckFlashTimerRef.current);
                }
                deckFlashTimerRef.current = setTimeout(() => {
                    setIsDeckFlashing(false);
                    deckFlashTimerRef.current = null;
                }, 5000);
            }
        }
        
        // 現在の値を記録
        prevValuesRef.current = {
            consciousness: currentConsciousness,
            scale: currentScale,
            deckLength: currentDeckLength
        };
    }, [playerConsciousness, playerScale, playerId, triggerEffect, player, deckLength]);

    if (!player) return <div className="player-stats">{getUILoc('MESSAGES.LOADING')}</div>;

    const effectiveScale = getEffectiveScale(player, gameState);
    const scaleDisplay = effectiveScale !== player.scale 
        ? `${effectiveScale} (${player.scale})` 
        : player.scale;

    // 場のカード数を計算（財カードのみ）
    const fieldCardCount = player.field.length;
    
    // 先攻後攻の判定
    const isFirstPlayer = gameState?.first_player === player.id;
    const turnOrderText = isFirstPlayer ? getPlayerName('FIRST') : getPlayerName('SECOND');

    const formatNumber = (num) => {
        if (num >= 10000) {
            return `${Math.floor(num / 1000)}k`;
        } else if (num >= 1000) {
            return `${(num / 1000).toFixed(1)}k`;
        }
        return num.toString();
    };

    const formatScaleDisplay = (scaleDisplay) => {
        if (typeof scaleDisplay === 'string' && scaleDisplay.includes('(')) {
            const match = scaleDisplay.match(/^(\d+)\s*\((\d+)\)$/);
            if (match) {
                const effectiveScaleVal = parseInt(match[1]);
                const originalScaleVal = parseInt(match[2]);
                return `${formatNumber(effectiveScaleVal)} (${formatNumber(originalScaleVal)})`;
            }
        }
        const numValue = typeof scaleDisplay === 'number' ? scaleDisplay : parseInt(scaleDisplay);
        return formatNumber(numValue);
    };

    const consciousnessDisplay = formatNumber(player.consciousness);
    const scaleDisplayFormatted = formatScaleDisplay(scaleDisplay);

    const handleExposedCardClick = (card) => {
        if (onCardClick) {
            onCardClick(card);
        }
    };

    const openPileDetail = (type) => {
        audioManager.playSE('select');
        setPileDetailType(type);
    };

    const handleStatHelpClick = (e, type) => {
        e.stopPropagation();
        audioManager.playSE('select');
        const rect = e.target.getBoundingClientRect();
        setHelpPosition({ x: rect.left, y: rect.bottom });
        setHelpTarget(type);
    };

    const closeHelp = () => {
        setHelpTarget(null);
    };

    // 山札・捨て札の内容を表示するモーダル
    const renderPileDetailModal = () => {
        if (!pileDetailType) return null;

        const pile = pileDetailType === 'deck' ? player.deck : player.discard;
        const totalCount = pile.length;
        
        // カードをグループ化してID順にソート
        const groupedCards = pile.reduce((acc, card) => {
            if (!acc[card.id]) {
                acc[card.id] = { ...card, count: 0 };
            }
            acc[card.id].count += 1;
            return acc;
        }, {});

        const sortedGroups = Object.values(groupedCards).sort((a, b) => a.id - b.id);

        return (
            <div className="player-details-overlay pile-detail-overlay" style={{ zIndex: 2000 }} onClick={() => setPileDetailType(null)}>
                <div className="player-details-modal pile-detail-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="player-details-header">
                        <h3>
                            {pileDetailType === 'deck' ? getUILoc('LABELS.DECK') : getUILoc('LABELS.DISCARD_PILE')} 
                            ({getUILoc('UNITS.CARDS', { count: totalCount })})
                        </h3>
                        <button className="close-button" onClick={() => setPileDetailType(null)}>×</button>
                    </div>
                    <div className="player-details-content">
                        <div className="pile-cards-grid">
                            {sortedGroups.map((group) => {
                                const probability = totalCount > 0 ? (group.count / totalCount) * 100 : 0;
                                return (
                                    <div 
                                        key={group.id} 
                                        className="pile-card-group"
                                        onClick={() => setSelectedDetailCard(group)}
                                    >
                                        <div className="pile-card-illustration-wrapper">
                                            <CardIllustration cardId={group.id} isMini={true} />
                                            <div className="pile-card-count-badge">×{group.count}</div>
                                            {pileDetailType === 'deck' && (
                                                <div className="pile-card-prob-badge">{probability.toFixed(2)}%</div>
                                            )}
                                        </div>
                                        <div className="pile-card-name">{getCardName(group.id)}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // 公開カードのレンダリング
    const renderExposedCards = (isWidescreen = false) => {
        if (!player.exposed_cards || player.exposed_cards.length === 0) {
            return (
                <div className={`exposed-cards-container ${isWidescreen ? 'widescreen' : ''}`}>
                    <h4>{getUILoc('LABELS.EXPOSED_CARDS')}</h4>
                    <div className="exposed-cards-empty">{getUILoc('MESSAGES.NO_EXPOSED_CARDS')}</div>
                </div>
            );
        }

        return (
            <div className={`exposed-cards-container ${isWidescreen ? 'widescreen' : ''}`}>
                <h4>{getUILoc('LABELS.EXPOSED_CARDS')}</h4>
                <div className="exposed-cards-scroll-area">
                    {player.exposed_cards.map((card, idx) => (
                        <div 
                            key={`${card.id}-${idx}`} 
                            className={`exposed-card-mini ${card.was_played ? 'was-played' : ''}`}
                            onClick={() => handleExposedCardClick(card)}
                            title={getCardName(card.id)}
                        >
                            <CardIllustration cardId={card.id} isMini={true} />
                            <div className="exposed-card-name-mini">{getCardName(card.id)}</div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="player-stats-container">
            <div className="player-stats" data-player-id={player.id}>
                <button 
                    className="player-name-button"
                    onClick={() => setShowDetails(!showDetails)}
                    title={getUILoc('BUTTONS.DETAIL')}
                >
                    <div className="player-name-line">
                        <span className="player-name-text">{player.name}</span>
                        <span className={`turn-order ${isFirstPlayer ? 'first-player' : 'second-player'}`}>
                            {turnOrderText}
                        </span>
                    </div>
                    <div className={`deck-count-line ${isDeckFlashing ? 'deck-flash-active' : ''}`}>
                        {getUILoc('LABELS.DECK')}{getUILoc('UNITS.CARDS', { count: player.deck.length })}
                    </div>
                </button>
                
                <div className="main-stats">
                    <div className="main-stat consciousness" onClick={(e) => handleStatHelpClick(e, 'consciousness')} style={{ cursor: 'pointer' }}>
                        <div className="stat-label">{getUILoc('LABELS.CONSCIOUSNESS')}</div>
                        <div className="stat-value" title={getUILoc('LABELS.EXACT_VALUE', { value: player.consciousness })}>{consciousnessDisplay}</div>
                    </div>
                    <div className="main-stat scale" onClick={(e) => handleStatHelpClick(e, 'scale')} style={{ cursor: 'pointer' }}>
                        <div className="stat-label">{getUILoc('LABELS.SCALE')}</div>
                        <div className="stat-value" title={getUILoc('LABELS.EXACT_VALUE', { value: scaleDisplay })}>{scaleDisplayFormatted}</div>
                    </div>
                </div>

                {onEndTurn && (
                    <button 
                        onClick={onEndTurn} 
                        disabled={!!gameState?.awaiting_input || gameState?.current_turn !== HUMAN_PLAYER_ID}
                        className="end-turn-button"
                        id="end_turn_button"
                    >
                        {getUILoc('BUTTONS.END_TURN')}
                    </button>
                )}

                {showDetails && (
                    <div className="player-details-overlay" onClick={() => setShowDetails(false)}>
                        <div className="player-details-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="player-details-header">
                                <h3>{getUILoc('MESSAGES.PLAYER_DETAIL_HEADER', { name: player.name })}</h3>
                                <button 
                                    className="close-button"
                                    onClick={() => setShowDetails(false)}
                                >
                                    ×
                                </button>
                            </div>
                            <div className="player-details-content">
                                <div className="detail-section">
                                    <h4>{getUILoc('LABELS.BASIC_INFO')}</h4>
                                    <div className="detail-stat-line" onClick={(e) => handleStatHelpClick(e, 'consciousness')} style={{ cursor: 'pointer' }}>
                                        <span className="detail-label">{getUILoc('LABELS.CONSCIOUSNESS')}:</span>
                                        <span className="detail-value">{player.consciousness}</span>
                                    </div>
                                    <div className="detail-stat-line" onClick={(e) => handleStatHelpClick(e, 'scale')} style={{ cursor: 'pointer' }}>
                                        <span className="detail-label">{getUILoc('LABELS.SCALE')}:</span>
                                        <span className="detail-value">{scaleDisplay}</span>
                                    </div>
                                    <div className="detail-stat-line">
                                        <span className="detail-label">{getUILoc('LABELS.FIELD_LIMIT')}:</span>
                                        <span className="detail-value">{player.field_limit}</span>
                                    </div>
                                </div>
                                <div className="detail-section">
                                    <h4>{getUILoc('LABELS.CARD_COUNT')}</h4>
                                    <div className="detail-stat-line">
                                        <span className="detail-label">{getUILoc('LABELS.DECK')}:</span>
                                        <span className="detail-value">
                                            {getUILoc('UNITS.CARDS', { count: player.deck.length })}
                                            {player.id === HUMAN_PLAYER_ID && (
                                                <button className="check-pile-button" onClick={() => openPileDetail('deck')}>
                                                    {getUILoc('BUTTONS.CHECK')}
                                                </button>
                                            )}
                                        </span>
                                    </div>
                                    <div className="detail-stat-line">
                                        <span className="detail-label">{getUILoc('LABELS.HAND')}:</span>
                                        <span className="detail-value">{getUILoc('UNITS.CARDS', { count: player.hand.length })}</span>
                                    </div>
                                    <div className="detail-stat-line">
                                        <span className="detail-label">{getUILoc('LABELS.DISCARD_PILE')}:</span>
                                        <span className="detail-value">
                                            {getUILoc('UNITS.CARDS', { count: player.discard.length })}
                                            {player.id === HUMAN_PLAYER_ID && (
                                                <button className="check-pile-button" onClick={() => openPileDetail('discard')}>
                                                    {getUILoc('BUTTONS.CHECK')}
                                                </button>
                                            )}
                                        </span>
                                    </div>
                                    <div className="detail-stat-line">
                                        <span className="detail-label">{getUILoc('LABELS.FIELD')}:</span>
                                        <span className="detail-value">{fieldCardCount}/{player.field_limit}</span>
                                    </div>
                                </div>
                                {renderExposedCards(false)}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ワイドスクリーン用サブ情報 */}
            {showSubInfo && (
                <div className="player-sub-info">
                    <div className="sub-info-stats">
                        <div className="sub-info-line">
                            <span className="sub-info-label">{getUILoc('LABELS.HAND')}:</span>
                            <span className="sub-info-value">{player.hand.length}</span>
                        </div>
                        <div className="sub-info-line">
                            <span className="sub-info-label">{getUILoc('LABELS.DISCARD_PILE')}:</span>
                            <span className="sub-info-value">
                                {player.discard.length}
                                {player.id === HUMAN_PLAYER_ID && (
                                    <button className="check-pile-button-mini" onClick={() => openPileDetail('discard')}>
                                        🔍
                                    </button>
                                )}
                            </span>
                        </div>
                    </div>
                    {renderExposedCards(true)}
                </div>
            )}

            {renderPileDetailModal()}

            {selectedDetailCard && (
                <CardDetail
                    card={selectedDetailCard}
                    cardDefs={gameState.card_definitions}
                    onClose={() => setSelectedDetailCard(null)}
                />
            )}

            {helpTarget && (
                <div
                    className="stat-help-tooltip"
                    style={{
                        position: 'fixed',
                        left: `${helpPosition.x}px`,
                        top: `${helpPosition.y + 5}px`,
                        zIndex: 3000
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="stat-help-content">
                        {helpTarget === 'consciousness' && getUILoc('HELP.CONSCIOUSNESS')}
                        {helpTarget === 'scale' && getUILoc('HELP.SCALE')}
                    </div>
                </div>
            )}

            {helpTarget && (
                <div
                    className="stat-help-overlay"
                    onClick={closeHelp}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 2999
                    }}
                />
            )}
        </div>
    );
};

export default PlayerStats;
